import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Select from "../../Common/FormElements/Select/Select";
import { fetchOngoingProjects } from "../../../REDUX/actions/appProjects";
import { linkCartToQuote } from "../../../REDUX/actions/catalogue";
import useCanSeePrices from "../../../hooks/useCanSeePrices";
import {
  formatEuro,
  QUOTE_STATES,
  quoteStateOf,
} from "../../AppCustomers/Projects/QuotePill";

/**
 * Scénario B D1 — cart sidebar, every step of the order funnel: the cart
 * (partner purchase order) is linked to an ongoing customer project and,
 * optionally, one of its quotes; SOFT shows the margin between the quote
 * (selling price) and this order (partner purchase). Never shown to the
 * customer. "Passer la commande" (project › Devis) opens a cart already
 * linked.
 */
const CartQuoteLink = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { cart } = useSelector((state) => state.catalogueReducer);
  // A commercial links its cart to its project but never sees the margin.
  const canSeePrices = useCanSeePrices();
  const [projects, setProjects] = useState([]);
  // Any cart of the store ("commande particulier" too); an app order (fromApp)
  // is the customer's own order, not the store's purchase.
  const partnerCart = !!cart?.id && !cart.fromApp;

  useEffect(() => {
    if (!partnerCart) return;
    const controller = new AbortController();
    dispatch(fetchOngoingProjects(controller.signal)).then(setProjects);
    return () => controller.abort();
  }, [dispatch, partnerCart]);

  const project = projects.find((p) => p.id === cart?.project?.id);
  const quotes = (project?.quotes ?? []).filter((q) => quoteStateOf(q) !== "draft");
  const quote = quotes.find((q) => q.id === cart?.quote?.id);

  const projectOptions = projects.map((p) => ({
    name: `N°${p.number ?? p.id} — ${p.name ?? ""} — ${[
      p.mainContact?.firstName,
      p.mainContact?.lastName,
    ]
      .filter(Boolean)
      .join(" ")}`,
    code: p.id,
  }));
  const quoteOptions = quotes.map((q) => ({
    name: `${t("Quote no. {{number}} — {{amount}} incl. VAT", {
      number: q.id,
      amount: formatEuro(q.totalAmount),
    })} (${t(QUOTE_STATES[quoteStateOf(q)]?.label ?? "Sent")})`,
    code: q.id,
  }));

  const link = (projectId, quoteId) =>
    dispatch(linkCartToQuote(cart.id, { projectId, quoteId }, t));

  // Same rule as the Client list (API commande/utils/partnerOrder): HT, the
  // live sale's amount once ordered, else the quote's (TTC / 1.2).
  const liveSale = quote?.order?.state !== "cancelled" ? quote?.order : null;
  const sale =
    Number(liveSale?.totalHT) || (Number(quote?.totalAmount) || 0) / 1.2;
  const purchase = Number(cart?.totalHT) || 0;
  const margin = sale - purchase;

  if (!partnerCart) return null;
  return (
    <div className="border br10 p-3 my-3">
      <div className="row g-3">
        <div className="col-12">
          <label className="f14 sBold mb-1">{t("Customer project")}</label>
          <Select
            filter
            showClear
            resetable
            options={projectOptions}
            value={projectOptions.find((o) => o.code === cart?.project?.id) ?? null}
            callback={(e) => link(e.value?.code ?? null, null)}
          />
        </div>
        <div className="col-12">
          <label className="f14 sBold mb-1">{t("Quote")}</label>
          <Select
            showClear
            resetable
            disabled={!project}
            options={quoteOptions}
            value={quoteOptions.find((o) => o.code === cart?.quote?.id) ?? null}
            callback={(e) => link(cart.project?.id, e.value?.code ?? null)}
          />
        </div>
      </div>
      {quote && canSeePrices && (
        <div className="d-flex flex-column gap-2 mt-3 p-3 br10 bg-dimGrey f14">
          <span>
            {t("Selling price (quote no. {{number}})", { number: quote.id })}{" "}
            <b>{formatEuro(sale)} {t("excl. VAT")}</b>
          </span>
          <span>
            {t("Partner purchase (this order)")}{" "}
            <b>{formatEuro(purchase)} {t("excl. VAT")}</b>
          </span>
          <span>
            {t("Margin · Margin rate")}{" "}
            <b>
              {formatEuro(margin)} · {sale > 0 ? Math.round((margin / sale) * 100) : 0} %
            </b>
          </span>
        </div>
      )}
    </div>
  );
};

export default CartQuoteLink;
